/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/userDetails.scss';

import React, {FocusEventHandler, MouseEventHandler, useContext, useMemo} from 'react';
import User from "../../../../models/userModel";
import {
    CommonAdminErrors, Country, DataMode, SubscriptionStatus, UserStatus, UserType
} from "../../../../util/interfaces";
import {
    AwesomeIcon, capitalize, ChangeFunction, ChangeKeyFunction, ErrorType, Link, Loader, SelectItemShape,
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import Account from "../../../../models/accountModel";
import classNames from 'classnames';
import {AppContext} from "../../../misc/appContext";
import {BolmateButton, BolmateInput, BolmateSelect} from "../../../controls";
import CountryField from "../../../controls/country/field/countryField";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import BolmateSwitch from "../../../controls/switch/bolmateSwitch";

interface Props {
    onChange: ChangeKeyFunction;
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onClickAccount: (account: Account) => MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onClickLock: MouseEventHandler;
    onResetError: (key: keyof CommonAdminErrors) => FocusEventHandler;
    onChangeMode: (mode: DataMode) => MouseEventHandler;
    onChangeSubscriptionStatus: ChangeFunction<SubscriptionStatus>;
    onResetTwoFactor: MouseEventHandler;
    onChangeOverride: ChangeFunction<boolean>;
    errors: CommonAdminErrors;
    mode: DataMode;
    user: User;
    loading: boolean;
    accounts: Account[];
}

const subscriptionItems: SelectItemShape[] = [
    {label: 'Actief', value: SubscriptionStatus.ACTIVE},
    {label: 'Geannuleerd', value: SubscriptionStatus.CANCELED},
    {label: 'Gratis', value: SubscriptionStatus.FREE},
    {label: 'Inactief', value: SubscriptionStatus.INACTIVE},
    {label: 'Onbetaald', value: SubscriptionStatus.UNPAID},
    {label: 'Proefperiode', value: SubscriptionStatus.TRIAL},
];

export default function UserDetails(props: Props) {
    const {
        user, errors, mode, onChangeMode, loading, onChange, onResetError, onClickDelete, onClickLock,
        onClickAccount, onClose, onSubmit, onChangeSubscriptionStatus, accounts, onResetTwoFactor, onChangeOverride
    } = props;
    const context = useContext(AppContext);
    const userIsAdmin = context.userContext.user?.isAdmin() ?? false;

    const groupItems = useMemo(() => {
        const items: SelectItemShape[] = [
            {label: 'Gebruiker', value: UserType.USER},
            {label: 'Support', value: UserType.SUPPORT},
            {label: 'Premium gebruiker', value: UserType.PREMIUM},
            {label: 'Admin', value: UserType.ADMIN},
        ];
        if (context.userContext.user?.type === UserType.SUPER_ADMIN)
            items.push({label: 'Super admin', value: UserType.SUPER_ADMIN});
        return items;
    }, [context.userContext.user?.type])

    const lockIcon = useMemo(() => {
        let iconName: IconName = 'lock-open';
        if (user.status === UserStatus.ACTIVE) iconName = 'lock';
        return <AwesomeIcon name={iconName}/>;
    }, [user]);

    const lockLabel = useMemo(() => {
        return user.status === UserStatus.ACTIVE ? 'Blokkeren' : 'Deblokkeren';
    }, [user]);

    function renderHeader() {
        return (
            <div className='header-container'>
                <div className='header-items-container'>
                    <BolmateButton onClick={onClose} disabled={loading} label='Terug'
                                   ornaments={{element: <AwesomeIcon name='chevron-left'/>}}/>
                    <label className='header-title'>Gebruiker details</label>
                </div>
                <div className='header-items-container'>
                    {userIsAdmin ? <>
                        <BolmateButton onClick={onClickLock} label={lockLabel} ornaments={{element: lockIcon}}/>
                        <BolmateButton onClick={onClickDelete} label='Verwijderen' red secondary
                                       ornaments={{element: <AwesomeIcon name='trash-alt'/>}}/>
                    </> : <div/>}
                </div>
            </div>
        )
    }

    function renderBody() {
        return (
            <div className='body-container'>
                {userIsAdmin && (mode === DataMode.DETAILS ?
                    <BolmateButton onClick={onChangeMode(DataMode.EDIT)} label='Wijzigen'/> :
                    <BolmateButton onClick={onChangeMode(DataMode.DETAILS)} label='Annuleren'/>)}
                <div className='fields-container'>
                    <Loader active={loading}/>
                    {renderGroupField()}
                    <div className='detail-field'>
                        <span className={classNames('key', mode === DataMode.EDIT && 'edit')}>Status</span>
                        <span className='value'>{user.translatedStatus}</span>
                    </div>
                    {renderField('firstname', 'Voornaam', user.firstname, errors.firstname, 'firstname')}
                    {renderField('lastname', 'Achternaam', user.lastname, errors.lastname, 'lastname')}
                    {renderField('address', 'Adres', user.address, errors.address, 'address')}
                    {renderField('zip_code', 'Postcode', user.zipCode, errors.zipCode, 'zipCode')}
                    {renderField('city', 'Stad', user.city, errors.city, 'city')}
                    {renderCountryField(user.country)}
                    {renderField('email', 'E-mailadres', user.email, errors.email, 'email')}
                    {renderField('tel_number', 'Telefoonnummer', user.telNumber, errors.telNumber,
                        'telNumber')}
                    <div className='detail-field col-3'>
                        <span
                            className={classNames('key', mode === DataMode.EDIT && 'edit')}>Tweestapsverificatie</span>
                        <span className='value'>{user.twoFactorEnabled ? 'Aan' : 'Uit'}</span>
                        {user.twoFactorEnabled && <Link onClick={onResetTwoFactor}>Resetten</Link>}
                    </div>
                    {renderSubscriptionStatus()}
                    {renderTrial()}
                    {renderOverride()}
                </div>
                {renderAccounts()}
            </div>
        );
    }

    function renderGroupField() {
        if (mode === DataMode.DETAILS) return (
            <div className='detail-field'>
                <span className='key'>Type</span>
                <span className='value'>{user.translatedType}</span>
            </div>
        );
        else return (
            <BolmateSelect value={user.type} items={groupItems} onChange={onChange('type')} label='Type'
                           className='edit-field'/>
        );
    }

    function renderField(key: string, label: string, value: any, error?: ErrorType,
                         errorKey?: keyof CommonAdminErrors) {
        if (mode === DataMode.DETAILS) return (
            <div className='detail-field'>
                <span className='key'>{label}</span>
                <span className='value'>{value}</span>
            </div>
        );
        else {
            const onFocus = errorKey ? onResetError(errorKey) : undefined;
            return (
                <BolmateInput value={value} onChange={onChange(key)} label={label} error={error}
                              onFocus={onFocus} disabled={loading} className='edit-field'/>
            );
        }
    }

    function renderCountryField(country: Country) {
        if (mode === DataMode.DETAILS) return (
            <div className='detail-field'>
                <span className='key'>Land</span>
                <span className='value'>{country}</span>
            </div>
        );
        return (
            <CountryField onChange={onChange('country')} value={country} labelAsValue/>
        );
    }

    function renderSubscriptionStatus() {
        if (mode === DataMode.DETAILS) return (
            <>
                <div className='detail-field'>
                    <span className='key'>Abonnement</span>
                    <span className='value'>{user.subscription?.statusLabel ?? 'Geen abonnement'}</span>
                </div>
                <div className='detail-field'>
                    <span className='key'>Stripe ID</span>
                    <span className='value'>{user.subscription?.customerId ?? '-'}</span>
                </div>
            </>
        )
        return (
            <BolmateSelect value={user.subscription?.status ?? SubscriptionStatus.INACTIVE}
                           onChange={onChangeSubscriptionStatus} items={subscriptionItems} label='Status abonnement'/>
        )
    }

    function renderTrial() {
        const trialEnd = user.subscription?.trialEndDate;
        if (!trialEnd) return null;
        const days = trialEnd.endOf('day').diff(IDate.now(), 'days');
        const daysText = days >= 0 ? ` (${days} dag${days === 1 ? '' : 'en'})` : null;
        return (
            <div className='detail-field'>
                <span className={classNames('key', mode === DataMode.EDIT && 'edit')}>Proefperiode t\m</span>
                <span className='value'>{trialEnd.format('DD MMMM')}{daysText}</span>
            </div>
        )
    }

    function renderOverride() {
        if (mode === DataMode.DETAILS) return (
            <div className='detail-field'>
                <span className='key'>Negeer beperkingen</span>
                <span className='value'>{user.overrideLimitations ? 'Ja' : 'Nee'}</span>
            </div>
        )
        return (
            <BolmateSwitch value={user.overrideLimitations} onChange={onChangeOverride} mainLabel='Negeer beperkingen'
                           activeRed/>
        );
    }

    function renderAccounts() {
        return (
            <div className='accounts-container'>
                <label className='accounts-title'>Accounts</label>
                <div className='accounts'>
                    <div className='row head'>
                        <label>AANGEMAAKT OP</label>
                        <label>BEDRIJFSNAAM</label>
                        <label>STATUS</label>
                    </div>
                    {accounts.map(renderAccount)}
                </div>
            </div>
        )
    }

    function renderAccount(account: Account) {
        return (
            <div className='row' onClick={onClickAccount(account)} key={account.id}>
                <label>{account.createdAtMoment.format('DD-MM-YYYY HH:mm')}</label>
                <label>{account.selectablePlatformLabel}</label>
                <label>{capitalize(account.translatedStatus)}</label>
            </div>
        )
    }

    function renderFooter() {
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onClose} label='Sluiten'/>
                {userIsAdmin ? <BolmateButton onClick={onSubmit} label='Opslaan'/> : <div/>}
            </div>
        )
    }

    return (
        <div
            className='user-details'>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    )
}